import type { RouteSegment, StreetNodePlanningData, StreetSegmentPlanningData } from '@/api/main/types.gen'
import { findShortestPath, type RoutingSource } from './shortestPath'

export type GapFix = {
  insertAt: number
  removeCount: number
  bridge: RouteSegment[]
}

/**
 * Collects node ids involved in route integrity problems: missing planning segments,
 * nodes absent from the area graph, consecutive segments that do not share an exit→entrance
 * node, or a `lastNode` that does not match the final segment exit.
 */
export function detectRouteGapNodeIds(
  segments: RouteSegment[],
  lastNode: string | undefined,
  indexedSegments: Record<string, StreetSegmentPlanningData>,
  streetNodes: Record<string, StreetNodePlanningData>
): Set<string> {
  const ids = new Set<string>()
  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i]!
    if (!indexedSegments[seg.street_segment_id]) {
      if (seg.entrance_node_id) ids.add(seg.entrance_node_id)
      if (seg.exit_node_id) ids.add(seg.exit_node_id)
    }
    if (seg.entrance_node_id && !streetNodes[seg.entrance_node_id]) ids.add(seg.entrance_node_id)
    if (seg.exit_node_id && !streetNodes[seg.exit_node_id]) ids.add(seg.exit_node_id)
    if (i + 1 < segments.length) {
      const next = segments[i + 1]!
      if (seg.exit_node_id !== next.entrance_node_id) {
        if (seg.exit_node_id) ids.add(seg.exit_node_id)
        if (next.entrance_node_id) ids.add(next.entrance_node_id)
      }
    }
  }
  const lastSeg = segments[segments.length - 1]
  if (lastSeg && lastNode && lastSeg.exit_node_id !== lastNode) {
    ids.add(lastNode)
  }
  return ids
}

/**
 * Suggests shortest-path bridges for fixable gaps only: a missing segment whose endpoints
 * still exist (`removeCount: 1`), or a disconnect between consecutive segments (`removeCount: 0`).
 * Skips gaps where endpoints are unknown or no path exists.
 */
export function computeGapFixes(
  segments: RouteSegment[],
  indexedSegments: Record<string, StreetSegmentPlanningData>,
  streetNodes: Record<string, StreetNodePlanningData>,
  routableSegments: RoutingSource
): GapFix[] {
  const fixes: GapFix[] = []
  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i]!
    if (
      !indexedSegments[seg.street_segment_id] &&
      seg.entrance_node_id &&
      seg.exit_node_id &&
      streetNodes[seg.entrance_node_id] &&
      streetNodes[seg.exit_node_id]
    ) {
      const bridge = findShortestPath(routableSegments, seg.entrance_node_id, seg.exit_node_id)
      if (bridge) {
        fixes.push({ insertAt: i, removeCount: 1, bridge })
        continue
      }
    }
    if (i + 1 < segments.length) {
      const next = segments[i + 1]!
      if (
        seg.exit_node_id !== next.entrance_node_id &&
        seg.exit_node_id &&
        next.entrance_node_id &&
        streetNodes[seg.exit_node_id] &&
        streetNodes[next.entrance_node_id]
      ) {
        const bridge = findShortestPath(routableSegments, seg.exit_node_id, next.entrance_node_id)
        if (bridge) fixes.push({ insertAt: i + 1, removeCount: 0, bridge })
      }
    }
  }
  return fixes
}

/** Applies fixes from highest `insertAt` first so earlier indices stay valid after splices. */
export function applyGapFixesToSegments(segments: RouteSegment[], fixes: GapFix[]): RouteSegment[] {
  const segs = [...segments]
  const sorted = [...fixes].sort((a, b) => b.insertAt - a.insertAt)
  for (const fix of sorted) {
    segs.splice(fix.insertAt, fix.removeCount, ...fix.bridge)
  }
  return segs
}
